import React, { useCallback, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { useParams, useHistory } from "react-router-dom";

import FullScreenLoader from "../components/fullScreenLoader";
import Dashboard from "./dashboard";
import api from "../services/api";

//The big rabbit video
// const STREAM_URL = "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8";

export default function FullScreenHlsVideo() {
  const { token, url_alias } = useParams();

  const [cameraId, setCameraId] = useState();
  const [streamURL, setStreamURL] = useState();
  const [waterMark, setWaterMark] = useState();
  const [expiration, setExpiration] = useState();
  const [usageLimitReached, setUsageLimitReached] = useState(false);

  const videoRef = useRef(null);
  const timeoutRef = useRef();
  const usageHitTokenRef = useRef(null);

  const history = useHistory();

  const playVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const playPromise = video.play();

    if (playPromise?.catch) {
      playPromise.catch((err) => {
        console.info("Camera autoplay blocked:", err);
      });
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.autoplay = true;
    video.playsInline = true;

    if (Hls.isSupported()) {
      const hls = new Hls();

      hls.loadSource(streamURL);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, playVideo);

      return () => {
        hls.off(Hls.Events.MANIFEST_PARSED, playVideo);
        hls.destroy();
      };
    } else {
      // Safari (native HLS)
      video.src = streamURL;
      video.addEventListener("loadedmetadata", playVideo);

      return () => {
        video.removeEventListener("loadedmetadata", playVideo);
      };
    }
  }, [streamURL, playVideo]);

  const requestLocationHitsUpdate = async (locationId) => {
    try {
      const { data } = await api.patch(
        `/location/url-alias/usage/${locationId}`,
      );

      const {
        url_alias_daily_usage_hits,
        url_alias_daily_usage_limit,
        daily_usege_overcomed,
      } = data ?? {};

      console.log(
        `Camera usage limit updating locationId ${locationId} success...`,
      );
      console.log(
        `Camera usage stats: hits ${url_alias_daily_usage_hits} of ${url_alias_daily_usage_limit} limit, daily_usege_overcomed ${daily_usege_overcomed ? "reached." : "not reached."}. Success...`,
      );

      return Boolean(daily_usege_overcomed);
    } catch (err) {
      console.log(
        `Camera usage limit updating  locationId ${locationId} error...`,
      );
      console.error(err);

      return false;
    }
  };

  const requestCameraId = useCallback(async () => {
    //Token decode failure. Must try resolve url_alias
    try {
      const safeUrlAlias = url_alias || token;

      if (usageHitTokenRef.current !== safeUrlAlias) {
        setUsageLimitReached(false);
      }

      const response = await api.get(`/location/?url_alias=${safeUrlAlias}`);

      if (
        !response ||
        response?.length === 0 ||
        response?.error ||
        !response?.data?.[0]?.id
      ) {
        history.push("/notfound");

        return;
      }

      const location = response.data[0];

      const locationId = response.data[0]?.id;

      if (
        usageHitTokenRef.current !== safeUrlAlias &&
        Number.isFinite(Number(locationId)) &&
        Number(locationId) > 0
      ) {
        usageHitTokenRef.current = safeUrlAlias;

        const dailyUsageLimitReached =
          await requestLocationHitsUpdate(locationId);

        if (dailyUsageLimitReached) {
          setUsageLimitReached(true);

          return;
        }
      }

      const cameraId = location?.cameras[0]?.id;

      if (!cameraId) return; //Ethernal loading....

      document.title = location?.campaign?.title
        ? `Windradar - ${location?.campaign?.title}`
        : `Dashboard`;

      console.log("camera >>>>>>", cameraId);

      setCameraId(cameraId);
    } catch (e) {
      console.error("erro =>", e);
    }
  }, [url_alias, token, history]);

  const requestCameraToken = useCallback(async () => {
    try {
      const url = `/location/camera/token/${cameraId}`;

      console.log(url);

      const { data } = await api.get(url);

      if (!data?.stream_url) {
        throw new Error("stream_url not found");
      }
      console.log(data.stream_url);

      setStreamURL(data.stream_url);
      setExpiration(data.expiration_millis);
      setWaterMark(data.camera_watermark.url);
    } catch (e) {
      console.log("requestCameraToken failed:", e);
    }
  }, [cameraId]);

  useEffect(() => {
    if (!token && !url_alias) return;

    requestCameraId();
  }, [token, url_alias, requestCameraId]);

  useEffect(() => {
    if (!cameraId) return;

    requestCameraToken();
  }, [cameraId, requestCameraToken]);

  useEffect(() => {
    if (!expiration) return;

    // Clear any previous timeout
    clearTimeout(timeoutRef.current);

    const now = Date.now();

    // Refresh 1 minute BEFORE expiration
    const refreshAt = expiration - 60 * 1000;

    const delay = Math.max(refreshAt - now, 0);

    timeoutRef.current = setTimeout(() => {
      requestCameraToken();
    }, delay);

    return () => clearTimeout(timeoutRef.current);
  }, [expiration, requestCameraToken]);

  if (usageLimitReached) {
    return (
      <div style={styles.limitMessage}>
        <h1 style={styles.limitTitle}>Limite diário atingido</h1>
        <p style={styles.limitText}>
          O limite de acessos desta câmera foi atingido. Tente novamente mais
          tarde.
        </p>
        <p style={styles.limitText}>
          The access limit for this camera has been reached. Please try again
          later.
        </p>
      </div>
    );
  }

  if (!streamURL) {
    return <FullScreenLoader label="Carregando dados..." />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.dashboard}>
        <Dashboard />
      </div>
      <video
        ref={videoRef}
        controls
        autoPlay
        muted
        playsInline
        onLoadedMetadata={playVideo}
        style={styles.video}
      />
      <div style={styles.imageContainer}>
        <img
          src={waterMark}
          alt="Logo"
          style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 12 }}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────── */
/* Styles                       */
/* ─────────────────────────── */

const styles = {
  container: {
    width: "100vw",
    height: "100vh",
    background: "black",
    position: "relative",
  },
  dashboard: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "20vw",
    height: "100vh",

    zIndex: 10,

    /* CRITICAL */
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  button: {
    position: "absolute",
    bottom: 20,
    left: 20,
    padding: "10px 16px",
    cursor: "pointer",
  },
  imageContainer: {
    position: "absolute",
    top: 30,
    left: 0,

    width: "12vw",
    height: "12vh",

    opacity: 0.5,
    // backgroundColor: "rgba(0, 0, 0, 0.7)", // 70% opacity
    borderRadius: 16, // rounded borders

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    zIndex: 10,
  },
  limitMessage: {
    width: "100vw",
    height: "100vh",
    background: "black",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    textAlign: "center",
    fontFamily: "Montserrat",
  },
  limitTitle: {
    margin: 0,
    fontSize: 32,
    fontWeight: 700,
  },
  limitText: {
    maxWidth: 520,
    margin: "16px 0 0",
    fontSize: 18,
    lineHeight: 1.5,
  },
};
